import { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { toZonedTime } from 'date-fns-tz';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography,   
    CircularProgress, 
    TextField, 
    Button, 
    MenuItem, 
    Divider,
    Snackbar, 
    Alert, 
    Select, 
    FormControl, 
    InputLabel, 
    IconButton,
    FormControlLabel, 
    Checkbox, 
} from '@mui/material';
import DataGridComponent from 'components/DataGridComponent';
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { de, enGB } from 'date-fns/locale';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ClearIcon from "@mui/icons-material/Clear";
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { sortMultipleFields } from 'state/helpers';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const ADJUSTMENT_TYPES = [  
    'Adjustment',
    'Pay Discrepancy', 
    'Welfare', 
    'Zone Transfer', 
    'Candidate Referral', 
    'Ground Training',
    'Simulator Training',
    'Line Training',
    'Others',
];

const IncomeAdjustmentPage = () => {
    useAuthCheck();
    const { incomeAdjustmentId } = useParams();    
    const navigate = useNavigate();
    const locales = { 'en-us': undefined, 'en-gb': enGB, de };
    const crewRanksData = useSelector((state) => state.crewRanks);
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));


    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const [activeCrewMembers, setActiveCrewMembers] = useState([]);
    const [search, setSearch] = useState("");
    const [rankFilter, setRankFilter] = useState("");
    const [baseFilter, setBaseFilter] = useState("");
    const [deptFilter, setDeptFilter] = useState("");
    const [rowSelectionModel, setRowSelectionModel] = useState({
        type: 'include',
        ids: new Set(),
    });

    const sortedRankOptions = useMemo(() => {
        const rankMap = new Map((crewRanksData || []).map(r => [r.rankCode, r.rankPriority ?? 999]));
        return [...new Set(activeCrewMembers.map((e) => e.rank))]
            .sort((a, b) => (rankMap.get(a) ?? 999) - (rankMap.get(b) ?? 999));
    }, [activeCrewMembers, crewRanksData]);

    const filteredCrewMembers = useMemo(() => {
        return activeCrewMembers.filter((e) => {
            const matchSearch =
                !search ||
                e.crewCode.toLowerCase().includes(search) ||
                e.crewName.toLowerCase().includes(search.toLowerCase());
            const matchRank = !rankFilter || e.rank === rankFilter;
            const matchBase = !baseFilter || e.location === baseFilter;
            const matchDept = !deptFilter || e.department === deptFilter;
            return matchSearch && matchRank && matchBase && matchDept;
        });
    }, [activeCrewMembers, search, rankFilter, baseFilter, deptFilter]);

    const parseUTCDateTime = (datetime) => {
        if (!datetime) return null;
        const date = new Date(datetime);
        return new Date(Date.UTC(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            date.getHours(),
            date.getMinutes()
        ));
    };

    const [formValues, setFormValues] = useState({
        description: "",  
        adjustmentType: "Adjustment",
        effectiveDate: new Date(),
        amount: 0,
        currency: 'VND',
        crewMembers: [],  
        isNonTaxableIncome: false,  
    });
    
    
    const fetchCrewMembers = useCallback(async () => {
        return await api.get('/crewmembers/active');
    }, []);

    const fetchIncomeAdjustment = useCallback(async () => {
        if (incomeAdjustmentId) {
            const url = `/incomeadjustments/${incomeAdjustmentId}`;
            return await api.get(url);            
        }
    }, [incomeAdjustmentId]);
    
    useEffect(() => {
        const fetchData = async () => {
            try {
                setError(null);
                setLoading(true);
                
                const [
                    crewMembersResponse, 
                    incomeAdjustmentResponse
                ] = await Promise.all([
                    fetchCrewMembers(),
                    incomeAdjustmentId ? fetchIncomeAdjustment() : Promise.resolve(null),
                ]);
                       
                if (crewMembersResponse.data) {
                    const sortedCrewMembers = sortMultipleFields(
                        crewMembersResponse.data, 
                        [
                            { field: 'rank', direction: 'asc' },
                            { field: 'location', direction: 'asc' },
                            { field: 'crewCode', direction: 'asc' }, 
                        ], 
                        true,
                    );
                    setActiveCrewMembers(sortedCrewMembers);
                };                

                const formData = incomeAdjustmentResponse?.data;   
                if (formData?.crewMembers?.length) {
                    const ids  = new Set(
                        crewMembersResponse.data
                            ?.filter(cm => formData.crewMembers.some(s => s.crewCode === cm.crewCode))
                            ?.map(cm => cm._id)
                    );
                    
                    setRowSelectionModel({ type: 'include', ids });
                }             

                setFormValues({                    
                    description: formData?.description || "",
                    adjustmentType: formData?.adjustmentType || "Adjustment",
                    effectiveDate: formData?.effectiveDate ? toZonedTime(new Date(formData.effectiveDate), 'UTC') : null,
                    amount: formData?.amount || 0,
                    currency: formData?.currency || 'VND',
                    crewMembers: formData?.crewMembers || [],
                    isNonTaxableIncome: formData?.isNonTaxableIncome ?? false,
                });
                
            } catch (error) {
                setError(error);
                toast.error(error.response?.data?.message || "Failed to fetch data!");
            } finally {
                setLoading(false);
            }
        };
    
        fetchData();      
    }, [fetchCrewMembers, 
        fetchIncomeAdjustment, 
        incomeAdjustmentId
    ]); 

    const isSameSet = (a, b) => {
        if (a === b) return true;
        if (!a || !b) return false;
        if (a.size !== b.size) return false;
        for (const v of a) if (!b.has(v)) return false;
        return true;
    };

    const handleRowSelectionChange = useCallback((newModel) => {
        if (!newModel?.ids) return;

        setRowSelectionModel(prev => {
            const nextIds = new Set(prev.ids);
            const visibleIds = filteredCrewMembers.map(r => r._id);

            // remove unchecked in current view
            visibleIds.forEach(id => {
                if (prev.ids.has(id) && !newModel.ids.has(id)) {
                    nextIds.delete(id);
                }
            });

            // add newly checked
            newModel.ids.forEach(id => nextIds.add(id));

            // Nếu không đổi → KHÔNG setState
            if (prev.type === 'include' && isSameSet(prev.ids, nextIds)) {
                return prev;
            }

            return {
                type: 'include',
                ids: nextIds,
            };
        });
    }, [filteredCrewMembers]);
    
    const checkoutSchema = yup.object().shape({
        description: yup.string().required('Required'),
        adjustmentType: yup.string().required('Required'),
        effectiveDate: yup.string().required('Required'),
    });

    const handleFormSubmit = async (values, onSubmitProps) => {        
        try {
            if (rowSelectionModel.ids.size === 0) {
                setSnackbar({ 
                    open: true, 
                    message: "No crew members selected", 
                    severity: "warning" 
                });
                return;
            }
            if (values.amount === "" || isNaN(Number(values.amount))) {
                setSnackbar({ open: true, message: "Invalid adjustment value", severity: "error" });
                return;
            }
            
            const payload = {
                ...values,
                effectiveDate: values.effectiveDate
                    ? parseUTCDateTime(values.effectiveDate) 
                    : null,
                crewMembers: activeCrewMembers
                    .filter((c) => rowSelectionModel.ids.has(c._id))
                    .map((c) => ({
                        crewCode: c.crewCode,
                        crewName: c.crewName,
                        rank: c.rank,
                        base: c.location,
                        division: c.division,
                        department: c.department,
                    })),
            }; 
            const url = incomeAdjustmentId 
                ? `/incomeadjustments/update/${incomeAdjustmentId}` 
                : '/incomeadjustments/new';      
            const method = incomeAdjustmentId ? 'put' : 'post';
            
            const response = await api({
                method,
                url,
                data: payload,
            });

            if (![200, 201].includes(response.status)) {
                toast.error("Failed to submit the form!");
                return;
            }

            // toast.success(
            //     incomeAdjustmentId
            //         ? 'Income adjustment updated successfully.' 
            //         : 'Income adjustment created successfully.'
            // );        
            setSnackbar({
                open: true,
                message: `Adjustment applied to ${rowSelectionModel.ids.size} crew members`,
                severity: "success",
            });
            
            navigate("/incomeadjustments");
        } catch (error) {
            setError(error);
            toast.error(error.message || "Failed to submit the form!");
        }
    };

    const columns = [
        { field: "crewCode", headerName: "Code", flex: 1 },
        { field: "crewName", headerName: "Name", flex: 5 },
        { field: "rank", headerName: "Rank", flex: 1.2 },        
        { field: "location", headerName: "Base", flex: 1.2 },        
        { field: "division", headerName: "Division", flex: 3 },        
        { field: "department", headerName: "Department", flex: 3 },        
    ];


    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="Income Adjustment"
                    subtitle={`${loading ? 'Loading...' : 
                        incomeAdjustmentId 
                            ? 'Update' 
                            : 'Create'}`
                    }
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/incomeadjustments")}
                        >
                            <ArrowBackIosNewIcon />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <LocalizationProvider 
                        dateAdapter={AdapterDateFns}
                        adapterLocale={locales['en-gb']}
                    >
                        <Formik
                            initialValues={formValues}
                            enableReinitialize={true}
                            validationSchema={checkoutSchema}
                            onSubmit={handleFormSubmit}
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleBlur,
                                handleChange,
                                handleSubmit,
                                setFieldValue,
                            }) => (
                                <form onSubmit={handleSubmit}>
                                    <Box
                                        display="grid"
                                        gap={2}
                                        gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                    >          
                                        <TextField
                                            required
                                            fullWidth
                                            size="small"
                                            variant="filled"
                                            // multiline
                                            // rows={2}
                                            label="Description"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.description}
                                            name="description"
                                            error={!!touched.description && !!errors.description}
                                            helperText={touched.description && errors.description}
                                            sx={{ gridColumn: 'span 6' }}
                                        />
                                        <TextField
                                            required
                                            fullWidth
                                            size="small"
                                            variant="filled"
                                            label="Adjustment Type"
                                            name="adjustmentType"
                                            select
                                            value={values.adjustmentType || "Adjustment"}
                                            onChange={(e) => setFieldValue("adjustmentType", e.target.value)}
                                            sx={{ gridColumn: "span 6" }}
                                        >
                                            {ADJUSTMENT_TYPES.map((type) => (
                                                <MenuItem key={type} value={type}>
                                                    {type}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        
                                        <Box sx={{ gridColumn: "span 3", position: 'relative' }}>
                                            <DatePicker
                                                label="Effective Date"
                                                name="effectiveDate"
                                                value={values.effectiveDate}
                                                maxDate={values.effectiveDate}
                                                format = "dd/MM/yyyy"
                                                touched={touched}
                                                errors={errors}
                                                onChange={(newValue) => {
                                                    setFieldValue("effectiveDate", newValue);                                               
                                                }}                                                                                           
                                                sx={{ gridColumn: "span 6" }}                                                
                                                slotProps={{
                                                    textField: {
                                                        required: true,
                                                        fullWidth: true,
                                                        variant: "filled",
                                                        size: "small",
                                                    },
                                                }}
                                            />
                                            {values.effectiveDate && (
                                                <IconButton 
                                                    onClick={() => {
                                                        setFieldValue("effectiveDate", null);                                               
                                                    }}
                                                    aria-label="clear date"
                                                    sx={{ 
                                                        position: 'absolute',
                                                        right: 35, 
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        '&:hover': { backgroundColor: 'action.hover' }
                                                    }}
                                                >
                                                    <ClearIcon />
                                                </IconButton>
                                            )}
                                        </Box>
                                        
                                        <TextField
                                            required
                                            fullWidth
                                            size="small"
                                            variant="filled"
                                            type="number"
                                            label="Amount"
                                            name="amount"
                                            value={values.amount}
                                            onChange={handleChange}
                                            slotProps={{
                                                htmlInput: {
                                                    // min: 0,
                                                    style: { textAlign: "right" },
                                                },
                                            }}                                         
                                            sx={{ gridColumn: 'span 3' }}
                                        />

                                        <TextField
                                            required
                                            fullWidth
                                            size="small"
                                            variant="filled"
                                            label="Currency"
                                            name="currency"
                                            select
                                            value={values.currency || "VND"}
                                            onChange={(e) => setFieldValue("currency", e.target.value)}
                                            sx={{ gridColumn: "span 3" }}
                                        >
                                            {["VND", "USD", "EUR", "GBP", "AUD", "JPY", "KRW"].map((cur) => (
                                                <MenuItem key={cur} value={cur}>
                                                    {cur}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        <FormControlLabel
                                            sx={{ gridColumn: isDesktop ? 'span 3' : 'span 6' }}
                                            control={
                                                <Checkbox
                                                    color="primary"
                                                    name="isNonTaxableIncome"
                                                    checked={values.isNonTaxableIncome}
                                                    onChange={handleChange}
                                                />
                                            }
                                            label="Is Non-Taxable Income"
                                        />
                                        
                                        <Divider sx={{ gridColumn: "span 12" }} />
                                        {/* Search and filter */}
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Search by Code or Name"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            sx={{ gridColumn: "span 3" }}
                                        />
                                        <FormControl size="small" sx={{ gridColumn: "span 3" }}>
                                            <InputLabel>Rank</InputLabel>
                                            <Select
                                                value={rankFilter}
                                                label="Rank"
                                                onChange={(e) => setRankFilter(e.target.value)}
                                            >
                                                <MenuItem value="">All</MenuItem>
                                                {sortedRankOptions.map((r) => (
                                                    <MenuItem key={r} value={r}>
                                                        {r}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl size="small" sx={{ gridColumn: "span 3" }}>
                                            <InputLabel>Base</InputLabel>
                                            <Select
                                                value={baseFilter}
                                                label="Base"
                                                onChange={(e) => setBaseFilter(e.target.value)}
                                            >
                                                <MenuItem value="">All</MenuItem>
                                                {[...new Set(activeCrewMembers.map((e) => e.location))].map((b) => (
                                                    <MenuItem key={b} value={b}>
                                                        {b}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl size="small" sx={{ gridColumn: "span 3" }}>
                                            <InputLabel>Department</InputLabel>
                                            <Select
                                                value={deptFilter}
                                                label="Department"
                                                onChange={(e) => setDeptFilter(e.target.value)}
                                            >
                                                <MenuItem value="">All</MenuItem>
                                                {[...new Set(
                                                    activeCrewMembers
                                                        .map(e => e.department)
                                                        .filter(d => d) 
                                                    )].map(d => (
                                                    <MenuItem key={d} value={d}>
                                                        {d}
                                                    </MenuItem>
                                                ))}

                                            </Select>
                                        </FormControl>
                                        
                                        <DataGridComponent
                                            rows={filteredCrewMembers}
                                            columns={columns}
                                            loading={loading}
                                            props={{
                                                checkboxSelection: true,
                                                disableRowSelectionOnClick: true,
                                                initialState: {
                                                    pagination: { paginationModel: { pageSize: 100 } },
                                                },
                                                rowSelectionModel,
                                                onRowSelectionModelChange: handleRowSelectionChange,
                                                columnHeaderHeight: 36,
                                                rowHeight: 30,
                                            }}
                                            sx={{ gridColumn: "span 12", height: '40vh' }}
                                        />

                                        <Button
                                            fullWidth
                                            type="submit"
                                            variant="contained"
                                            color="secondary"
                                            sx={{ gridColumn: "span 12", height: 42 }}                                           
                                        >
                                            {incomeAdjustmentId ? "Update" : "Create"}
                                        </Button>
                                    </Box>
                                </form>
                            )}
                        </Formik>
                    </LocalizationProvider>
                </Box>
            )}

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
};

export default IncomeAdjustmentPage;
